import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import {
  BESPRECHUNG_PUNKTARTEN,
  BESPRECHUNG_STAENDE,
  besprechungPunkteSortiert,
  type BesprechungPunkt,
  type BesprechungPunktart,
  type BesprechungTop,
  type SiteDocDetail,
} from '@symphenia/shared-types'
import { Feld as Spalte, ZeilenBlock, ZeilenListe, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock'
import { ZiehBereich, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { verschiebeVor } from '@/components/ziehen/ziehen'
import { datumOhneVorlage, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { fehlertext } from '@/lib/fehlertext'
import { punktAendern, punktAnlegen, punktLoeschen, tagesordnungSetzen } from './api'
import type { TeilnehmerVorschlag } from './TeilnehmerBlock'

const KUERZEL: Record<BesprechungPunktart, string> = { INFO: 'I', DECISION: 'B', TASK: 'A' }

/**
 * Wohin ein gezogener Punkt gehört (11.1): `reihe` ist die Folge der Abschnittsköpfe (`abschnitt:<key>`) und
 * Punkte, wie sie dastehen; der Punkt landet vor `vorKey` (`null` = ans Ende) und gehört zu dem Kopf, der
 * danach vor ihm steht — `null` für „ohne Tagesordnungspunkt".
 */
export function zielTagesordnungspunkt(reihe: readonly string[], key: string, vorKey: string | null): string | null {
  const neu = verschiebeVor(reihe, key, vorKey)
  const i = neu.indexOf(key)
  const kopf = [...neu.slice(0, i)].reverse().find((x) => x.startsWith('abschnitt:'))
  return kopf ? kopf.slice('abschnitt:'.length) || null : null
}
const neuerSchluessel = () => `top-${crypto.randomUUID()}`

// ── Tagesordnung ─────────────────────────────────────────────────────────────

/**
 * AP-49 5.2, 11.1 — **Tagesordnung**: Titel feldweise (eine Speicherstelle für die Liste als Ganzes, AP-36
 * 8.3 a), Reihenfolge durch Ziehen, die leere letzte Zeile legt einen Tagesordnungspunkt an.
 */
export function TagesordnungBlock({ dok, tops, onGeaendert }: { dok: SiteDocDetail; tops: BesprechungTop[]; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const [fehler, setFehler] = useState<string | null>(null)
  const [neu, setNeu] = useState('')
  const behaelter = useRef<HTMLDivElement>(null)
  const daten = useMemo<Datensatz>(() => Object.fromEntries(tops.map((x) => [x.key, x.titel])), [tops])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `besprechungTops:${dok.id}`,
    bezeichnung: t('siteDocs.meeting.agenda'),
    daten,
    schreibe: async (patch) => {
      const liste = tops.map((x) => ({ key: x.key, titel: typeof patch[x.key] === 'string' ? (patch[x.key] as string) : x.titel }))
      const r = await tagesordnungSetzen(dok.id, liste)
      onGeaendert(r)
      return Object.fromEntries(((r.content?.tagesordnung ?? []) as BesprechungTop[]).map((x) => [x.key, x.titel]))
    },
  }) as Speicherstelle<unknown>
  const setze = (liste: BesprechungTop[]) => {
    setFehler(null)
    tagesordnungSetzen(dok.id, liste).then(onGeaendert, (e) => setFehler(fehlertext(e, t)))
  }
  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      const reihe = verschiebeVor(tops.map((x) => x.key), key, vorKey)
      if (reihe.join() !== tops.map((x) => x.key).join()) setze(reihe.map((k) => tops.find((x) => x.key === k)!))
    },
  })
  const spalten: SpaltenDef[] = [
    { key: 'nr', label: t('siteDocs.meeting.number'), spur: '2.5rem', min: 40 },
    { key: 'titel', label: t('siteDocs.meeting.agendaItem'), spur: 'minmax(14rem,1fr)', min: 224 },
    { key: 'aktion', label: '', spur: '2rem', min: 32 },
  ]
  return (
    <section className="space-y-1">
      <h3 className="text-sm font-medium">{t('siteDocs.meeting.agenda')}</h3>
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
      <div ref={behaelter}>
        <ZiehBereich dndProps={dndProps} vorschau={gezogen && <span className="font-medium">{tops.find((x) => x.key === gezogen)?.titel}</span>}>
          <ZeilenListe spalten={spalten}>
            {tops.map((x, i) => (
              <TopZeile key={x.key} stelle={stelle} top={x} nr={i + 1} gezogen={gezogen === x.key} ziellinie={ziel && ziel.key === x.key ? ziel.kante : null} onLoeschen={() => setze(tops.filter((y) => y.key !== x.key))} />
            ))}
            <ZeilenBlock
              neu
              autoFokus={false}
              onVerwerfen={() => setNeu('')}
              onVerlassen={() => {
                if (!neu.trim()) return
                setze([...tops, { key: neuerSchluessel(), titel: neu.trim() }])
                setNeu('')
              }}
            >
              <Spalte spalte="nr" leer>
                <span className="px-1.5 text-sm text-muted-foreground">{tops.length + 1}</span>
              </Spalte>
              <Spalte spalte="titel">
                <input className={zelle} placeholder={t('siteDocs.meeting.newAgendaItem')} aria-label={t('siteDocs.meeting.newAgendaItem')} value={neu} onChange={(e) => setNeu(e.target.value)} />
              </Spalte>
              <Spalte spalte="aktion" leer>
                <span />
              </Spalte>
            </ZeilenBlock>
          </ZeilenListe>
        </ZiehBereich>
      </div>
    </section>
  )
}

function TopZeile({ stelle, top, nr, gezogen, ziellinie, onLoeschen }: { stelle: Speicherstelle<unknown>; top: BesprechungTop; nr: number; gezogen: boolean; ziellinie: 'oben' | 'unten' | null; onLoeschen: () => void }) {
  const { t } = useTranslation()
  const gruppe = useGruppe(stelle, { label: top.titel })
  const titel = useFeld(stelle, top.key, { gruppe, pflicht: true, label: t('siteDocs.meeting.agendaItem') })
  return (
    <ZeilenBlock gruppe={gruppe} schluessel={top.key} ziehbar gezogen={gezogen} ziellinie={ziellinie}>
      <Spalte spalte="nr">
        <span className="px-1.5 text-sm tabular-nums">{nr}</span>
      </Spalte>
      <Spalte spalte="titel">
        <input className={zelle} data-start aria-label={t('siteDocs.meeting.agendaItem')} {...titel.props} />
      </Spalte>
      <Spalte spalte="aktion">
        <button type="button" className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label={t('siteDocs.meeting.removeAgendaItem')} title={t('siteDocs.meeting.removeAgendaItem')} onClick={onLoeschen}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </Spalte>
    </ZeilenBlock>
  )
}

// ── Punkte ───────────────────────────────────────────────────────────────────

const alsDatensatz = (p: BesprechungPunkt): Datensatz => ({
  number: p.number,
  kind: p.kind,
  text: p.text,
  assigneeName: p.assigneeName ?? null,
  assigneeCompany: p.assigneeCompany ?? null,
  dueDate: p.dueDate ?? null,
  dueText: p.dueText ?? null,
  state: p.state ?? null,
})

interface PunktKontext {
  dok: SiteDocDetail
  vorschlaege: TeilnehmerVorschlag[]
  erledigt: Map<string, string>
  onGeaendert: (neu: SiteDocDetail) => void
  onFehler: (t: string | null) => void
}

function PunktZeile({ p, k, gezogen, ziellinie }: { p: BesprechungPunkt; k: PunktKontext; gezogen: boolean; ziellinie: 'oben' | 'unten' | null }) {
  const { t, i18n } = useTranslation()
  const daten = useMemo(() => alsDatensatz(p), [p])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `besprechungPunkt:${p.id}`,
    bezeichnung: t('siteDocs.meeting.itemLabel', { nr: p.number }),
    daten,
    schreibe: async (patch) => {
      const neu = await punktAendern(k.dok.id, p.id!, patch)
      k.onGeaendert(neu)
      const z = ((neu.content?.punkte ?? []) as BesprechungPunkt[]).find((x) => x.id === p.id)
      return z ? alsDatensatz(z) : daten
    },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: t('siteDocs.meeting.itemLabel', { nr: p.number }) })
  const aufgabe = p.kind === 'TASK'
  const nummer = useFeld(stelle, 'number', { gruppe, pflicht: true, label: t('siteDocs.meeting.number') })
  const art = useFeld(stelle, 'kind', { gruppe, art: 'auswahl', label: t('siteDocs.meeting.kind') })
  const text = useFeld(stelle, 'text', { gruppe, pflicht: true, label: t('siteDocs.meeting.text') })
  const wer = useFeld(stelle, 'assigneeName', {
    gruppe,
    label: t('siteDocs.meeting.assignee'),
    // Zuständig aus den Vorschlägen: Mitarbeiter des Betriebs werden zugeordnet (BS-5), die Firma geht mit.
    folge: (w) => {
      const v = k.vorschlaege.find((x) => x.name === w && x.userId) ?? k.vorschlaege.find((x) => x.name === w)
      return v ? { assigneeUserId: v.userId, assigneeCompany: v.company } : { assigneeUserId: null }
    },
  })
  const firma = useFeld(stelle, 'assigneeCompany', { gruppe, label: t('siteDocs.meeting.company') })
  const termin = useFeld(stelle, 'dueDate', { gruppe, art: 'datum', label: t('siteDocs.meeting.dueDate') })
  const terminText = useFeld(stelle, 'dueText', { gruppe, label: t('siteDocs.meeting.dueText') })
  const stand = useFeld(stelle, 'state', { gruppe, art: 'auswahl', label: t('siteDocs.meeting.state') })
  const liste = `besprechung-zustaendig-${k.dok.id}`
  const erledigtAm = p.originId && aufgabe && p.state === 'OPEN' ? k.erledigt.get(p.originId) : undefined
  return (
    <ZeilenBlock
      gruppe={gruppe}
      schluessel={p.id}
      ziehbar
      gezogen={gezogen}
      ziellinie={ziellinie}
      hinweis={
        p.carriedFrom || erledigtAm ? (
          <span className="flex flex-wrap items-center gap-2">
            {p.carriedFrom && <span>{p.carriedFrom}</span>}
            {erledigtAm && (
              <span className="text-amber-800 dark:text-amber-200">
                {t('siteDocs.meeting.doneInProject', { am: new Date(erledigtAm).toLocaleDateString(i18n.language) })}{' '}
                <button type="button" className="text-primary hover:underline" onClick={() => stand.setze('DONE')}>
                  {t('siteDocs.meeting.takeOverDone')}
                </button>
              </span>
            )}
          </span>
        ) : undefined
      }
    >
      <Spalte spalte="nummer">
        <input className={`${zelle} tabular-nums`} aria-label={t('siteDocs.meeting.number')} {...nummer.props} />
      </Spalte>
      <Spalte spalte="art">
        <select className={zelle} aria-label={t('siteDocs.meeting.kind')} {...art.props}>
          {BESPRECHUNG_PUNKTARTEN.map((a) => (
            <option key={a} value={a}>
              {KUERZEL[a]} – {t(`siteDocs.meeting.kinds.${a}`)}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="text">
        <input className={zelle} data-start aria-label={t('siteDocs.meeting.text')} {...text.props} />
      </Spalte>
      <Spalte spalte="wer">
        {aufgabe ? (
          <span className="flex flex-col gap-0.5">
            <input className={zelle} list={liste} aria-label={t('siteDocs.meeting.assignee')} {...wer.props} />
            <input className={`${zelle} text-xs`} placeholder={t('siteDocs.meeting.company')} aria-label={t('siteDocs.meeting.assigneeCompany')} {...firma.props} />
          </span>
        ) : (
          <span />
        )}
      </Spalte>
      <Spalte spalte="termin">
        {aufgabe ? (
          <span className="flex flex-col gap-0.5">
            <input type="date" className={`${zelle} ${datumOhneVorlage(termin.props.value)}`} aria-label={t('siteDocs.meeting.dueDate')} {...termin.props} />
            <input className={`${zelle} text-xs`} placeholder={t('siteDocs.meeting.dueTextPlaceholder')} aria-label={t('siteDocs.meeting.dueText')} {...terminText.props} />
          </span>
        ) : (
          <span />
        )}
      </Spalte>
      <Spalte spalte="stand">
        {aufgabe ? (
          <select className={zelle} aria-label={t('siteDocs.meeting.state')} {...stand.props}>
            {BESPRECHUNG_STAENDE.map((s) => (
              <option key={s} value={s}>
                {t(`siteDocs.meeting.states.${s}`)}
              </option>
            ))}
          </select>
        ) : (
          <span />
        )}
      </Spalte>
      <Spalte spalte="aktion">
        <button
          type="button"
          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          aria-label={t('siteDocs.meeting.removeItem')}
          title={t('siteDocs.meeting.removeItem')}
          onClick={() => {
            k.onFehler(null)
            punktLoeschen(k.dok.id, p.id!).then(k.onGeaendert, (e) => k.onFehler(fehlertext(e, t)))
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </Spalte>
    </ZeilenBlock>
  )
}

/** Die leere letzte Zeile eines Tagesordnungspunkts: Art und Text — beim Verlassen angelegt. */
function NeuerPunkt({ agendaKey, k }: { agendaKey: string | null; k: PunktKontext }) {
  const { t } = useTranslation()
  const [art, setArt] = useState<BesprechungPunktart>('INFO')
  const [text, setText] = useState('')
  const anlegen = async () => {
    if (!text.trim()) return
    k.onFehler(null)
    try {
      const r = await punktAnlegen(k.dok.id, { kind: art, text: text.trim(), agendaKey })
      setText('')
      k.onGeaendert(r)
    } catch (e) {
      k.onFehler(fehlertext(e, t))
    }
  }
  return (
    <ZeilenBlock neu autoFokus={false} onVerlassen={() => void anlegen()} onVerwerfen={() => setText('')}>
      <Spalte spalte="nummer" leer>
        <span />
      </Spalte>
      <Spalte spalte="art">
        <select className={zelle} aria-label={t('siteDocs.meeting.kind')} value={art} onChange={(e) => setArt(e.target.value as BesprechungPunktart)}>
          {BESPRECHUNG_PUNKTARTEN.map((a) => (
            <option key={a} value={a}>
              {KUERZEL[a]} – {t(`siteDocs.meeting.kinds.${a}`)}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="text">
        <input className={zelle} placeholder={t('siteDocs.meeting.newItem')} aria-label={t('siteDocs.meeting.newItem')} value={text} onChange={(e) => setText(e.target.value)} />
      </Spalte>
      <Spalte spalte="wer" leer>
        <span />
      </Spalte>
      <Spalte spalte="termin" leer>
        <span />
      </Spalte>
      <Spalte spalte="stand" leer>
        <span />
      </Spalte>
      <Spalte spalte="aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  )
}

/**
 * AP-49 5.2, 11.1 — **Punkte nach Tagesordnung** als Zeilenblock: Nummer, Art (I · B · A — auch per Taste),
 * Text, bei Aufgaben Zuständig (Vorschläge aus Teilnehmern und Mitarbeitern), Termin als Datum oder Text und
 * Status. Unter jedem Tagesordnungspunkt legt die leere letzte Zeile an; Punkte lassen sich zwischen den
 * Tagesordnungspunkten ziehen. Übernommene Punkte tragen ihre Herkunft.
 */
export function PunkteBlock({ dok, tops, punkte, vorschlaege, erledigt, onGeaendert }: { dok: SiteDocDetail; tops: BesprechungTop[]; punkte: BesprechungPunkt[]; vorschlaege: TeilnehmerVorschlag[]; erledigt: Map<string, string>; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const [fehler, setFehler] = useState<string | null>(null)
  const behaelter = useRef<HTMLDivElement>(null)
  const sortiert = besprechungPunkteSortiert(punkte, tops)
  const abschnitte = [...tops.map((x, i) => ({ key: x.key, titel: `${i + 1} ${x.titel}` })), { key: '', titel: tops.length ? t('siteDocs.meeting.furtherItems') : t('siteDocs.meeting.items') }]
  const gekannt = new Set(tops.map((x) => x.key))
  const imAbschnitt = (key: string) => sortiert.filter((p) => (key ? p.agendaKey === key : !p.agendaKey || !gekannt.has(p.agendaKey)))
  const reihe = abschnitte.flatMap((a) => [`abschnitt:${a.key}`, ...imAbschnitt(a.key).map((p) => p.id!)])
  const k: PunktKontext = { dok, vorschlaege, erledigt, onGeaendert, onFehler: setFehler }
  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      if (key.startsWith('abschnitt:')) return
      const agendaKey = zielTagesordnungspunkt(reihe, key, vorKey)
      const p = punkte.find((x) => x.id === key)
      if (!p || (p.agendaKey ?? null) === agendaKey) return
      setFehler(null)
      punktAendern(dok.id, key, { agendaKey }).then(onGeaendert, (e) => setFehler(fehlertext(e, t)))
    },
  })
  const spalten: SpaltenDef[] = [
    { key: 'nummer', label: t('siteDocs.meeting.number'), spur: '4rem', min: 64 },
    { key: 'art', label: t('siteDocs.meeting.kind'), spur: '8.5rem', min: 136 },
    { key: 'text', label: t('siteDocs.meeting.text'), spur: 'minmax(14rem,3fr)', min: 224 },
    { key: 'wer', label: t('siteDocs.meeting.assignee'), spur: 'minmax(9rem,1.5fr)', min: 144 },
    { key: 'termin', label: t('siteDocs.meeting.due'), spur: '9.5rem', min: 152 },
    { key: 'stand', label: t('siteDocs.meeting.state'), spur: '7.5rem', min: 120 },
    { key: 'aktion', label: '', spur: '2rem', min: 32 },
  ]
  const gezogenerPunkt = punkte.find((p) => p.id === gezogen)
  return (
    <section className="space-y-2">
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
      <datalist id={`besprechung-zustaendig-${dok.id}`}>
        {vorschlaege.map((v) => (
          <option key={`${v.contactId ?? ''}${v.userId ?? ''}${v.text}`} value={v.name}>
            {v.company ?? ''}
          </option>
        ))}
      </datalist>
      <div ref={behaelter} className="space-y-3">
        <ZiehBereich dndProps={dndProps} vorschau={gezogenerPunkt && <span className="font-medium">{gezogenerPunkt.number} {gezogenerPunkt.text}</span>}>
          {abschnitte.map((a) => (
            <div key={a.key || 'ohne'} className="space-y-1">
              <h3 data-key={`abschnitt:${a.key}`} className={`text-sm font-medium ${ziel?.key === `abschnitt:${a.key}` ? 'border-t-2 border-primary' : ''}`}>
                {a.titel}
              </h3>
              <ZeilenListe spalten={spalten}>
                {imAbschnitt(a.key).map((p) => (
                  <PunktZeile key={p.id ?? p.clientId} p={p} k={k} gezogen={gezogen === p.id} ziellinie={ziel && ziel.key === p.id ? ziel.kante : null} />
                ))}
                <NeuerPunkt agendaKey={a.key || null} k={k} />
              </ZeilenListe>
            </div>
          ))}
        </ZiehBereich>
      </div>
      <p className="text-xs text-muted-foreground">{t('siteDocs.meeting.itemsHint')}</p>
    </section>
  )
}
